import type { FC } from 'react';

import type { LlmVerdict, Platform } from '../../types';
import { AccountLevelNegativeCoverageEvidence } from './AccountLevelNegativeCoverageEvidence';
import { RestrictiveNegativesEvidence } from './RestrictiveNegativesEvidence';
import { AiMaxBrandExclusionsEvidence } from './AiMaxBrandExclusionsEvidence';
import { PmaxAdStrengthEvidence } from './PmaxAdStrengthEvidence';
import { PmaxAssetGroupCoverageEvidence } from './PmaxAssetGroupCoverageEvidence';
import { PmaxAudienceSignalsEvidence } from './PmaxAudienceSignalsEvidence';
import { PmaxSearchThemesEvidence } from './PmaxSearchThemesEvidence';
import { PmaxBrandExclusionsEvidence } from './PmaxBrandExclusionsEvidence';
import { PmaxFinalUrlExpansionEvidence } from './PmaxFinalUrlExpansionEvidence';
import { PmaxBrandGuidelinesEvidence } from './PmaxBrandGuidelinesEvidence';
import { PmaxToggleSettingEvidence } from './PmaxToggleSettingEvidence';
import { PmaxBidStrategyEvidence } from './PmaxBidStrategyEvidence';
import { PmaxBudgetConstraintsEvidence } from './PmaxBudgetConstraintsEvidence';
import { PmaxGoalSelectionEvidence } from './PmaxGoalSelectionEvidence';
import { PmaxStructureEvidence } from './PmaxStructureEvidence';
import { ShoppingCampaignPriorityEvidence } from './ShoppingCampaignPriorityEvidence';
import { ShoppingBidStrategyEvidence } from './ShoppingBidStrategyEvidence';
import { ShoppingBudgetConstraintsEvidence } from './ShoppingBudgetConstraintsEvidence';
import { ShoppingGoalSelectionEvidence } from './ShoppingGoalSelectionEvidence';
import { ShoppingBrandExclusionsEvidence } from './ShoppingBrandExclusionsEvidence';
import { ProductFeedHealthEvidence } from './ProductFeedHealthEvidence';
import { FeedAttributeCoverageEvidence } from './FeedAttributeCoverageEvidence';
import { TitleQualityEvidence } from './TitleQualityEvidence';
import { ListingGroupsEvidence } from './ListingGroupsEvidence';
import { PmaxListingGroupsEvidence } from './PmaxListingGroupsEvidence';
import { AccountIssuesEvidence } from './AccountIssuesEvidence';
import { AccountStructureEvidence } from './AccountStructureEvidence';
import { AdCopyAiEvidence } from './AdCopyAiEvidence';
import { AdCopyAiExperimentationEvidence } from './AdCopyAiExperimentationEvidence';
import { BiddingStrategyCoherenceEvidence } from './BiddingStrategyCoherenceEvidence';
import { BidModifierCleanupEvidence } from './BidModifierCleanupEvidence';
import { AttributionModelEvidence } from './AttributionModelEvidence';
import { AttributionModelMicrosoftEvidence } from './AttributionModelMicrosoftEvidence';
import { AudienceExclusionsEvidence } from './AudienceExclusionsEvidence';
import { AudienceLayeringCoverageEvidence } from './AudienceLayeringCoverageEvidence';
import { DefaultEvidence } from './DefaultEvidence';
import { DormantBiddingGoalsMicrosoftEvidence } from './DormantBiddingGoalsMicrosoftEvidence';
import { DormantDdaEvidence } from './DormantDdaEvidence';
import { DuplicateConversionEvidence } from './DuplicateConversionEvidence';
import { DuplicateConversionGoalsMicrosoftEvidence } from './DuplicateConversionGoalsMicrosoftEvidence';
import { GenericLlmJudgmentEvidence } from './GenericLlmJudgmentEvidence';
import { ImpressionShareLossEvidence } from './ImpressionShareLossEvidence';
import { MissingRemarketingCoverageEvidence } from './MissingRemarketingCoverageEvidence';
import { PrimaryConversionEvidence } from './PrimaryConversionEvidence';
import { SingleKeywordAdGroupsEvidence } from './SingleKeywordAdGroupsEvidence';
import { MatchTypeDistributionEvidence } from './MatchTypeDistributionEvidence';
import { DuplicateKeywordsEvidence } from './DuplicateKeywordsEvidence';
import { SmartBiddingLearningPeriodEvidence } from './SmartBiddingLearningPeriodEvidence';
import { StrategicPrimaryEvidence } from './StrategicPrimaryEvidence';
import { StrategicPrimaryMicrosoftEvidence } from './StrategicPrimaryMicrosoftEvidence';
import { UetConfigurationEvidence } from './UetConfigurationEvidence';

// EvidenceProps carries the whole evidence payload AND optional interpretive content (LLM
// verdict for judgment rows, "look here first" summary for data-table-heavy deterministic
// rows). Renderers decide whether/where to display each. The isJudgmentRow flag drives
// header treatment in FindingCard.
export interface EvidenceProps {
  evidence: Record<string, unknown>;
  llmVerdict: LlmVerdict | null;
  summary: string | null;
  isJudgmentRow: boolean;
}

// Dispatcher: a single check_id can map to platform-specific renderers when the underlying
// evidence shapes differ. Lookup order:
//   (checkId + platform) specific → checkId agnostic → judgment-row fallback → default.
const PLATFORM_SPECIFIC: Record<string, Partial<Record<Platform, FC<EvidenceProps>>>> = {
  'conversions.attribution_model': {
    google_ads: AttributionModelEvidence,
    microsoft_ads: AttributionModelMicrosoftEvidence
  },
  // The MS variants of these rows reframe the data shape (no per-action primary/secondary, no
  // 30d volume — see microsoft-conversions-parity-gap memory). Different evidence shapes
  // require different renderers even though the checkId is shared.
  'conversions.duplicate_names': {
    google_ads: DuplicateConversionEvidence,
    microsoft_ads: DuplicateConversionGoalsMicrosoftEvidence
  },
  'conversions.dormant_dda': {
    google_ads: DormantDdaEvidence,
    microsoft_ads: DormantBiddingGoalsMicrosoftEvidence
  },
  'conversions.primary_strategy': {
    google_ads: StrategicPrimaryEvidence,
    microsoft_ads: StrategicPrimaryMicrosoftEvidence
  }
};

const PLATFORM_AGNOSTIC: Record<string, FC<EvidenceProps>> = {
  'conversions.uet_configuration': UetConfigurationEvidence,
  'account_structure.overall_organization': AccountStructureEvidence,
  'conversions.primary_selection': PrimaryConversionEvidence,
  'ad_copy.adcopy_ai_adoption': AdCopyAiEvidence,
  'ad_copy.adcopy_ai_experimentation': AdCopyAiExperimentationEvidence,
  'bidding_budgets.strategy_coherence': BiddingStrategyCoherenceEvidence,
  'bidding_budgets.impression_share_loss': ImpressionShareLossEvidence,
  'bidding_budgets.bid_modifier_cleanup': BidModifierCleanupEvidence,
  'bidding_budgets.smart_bidding_learning_period': SmartBiddingLearningPeriodEvidence,
  'audiences.layering_coverage': AudienceLayeringCoverageEvidence,
  'audiences.missing_remarketing': MissingRemarketingCoverageEvidence,
  'audiences.exclusions': AudienceExclusionsEvidence,
  'keywords.single_keyword_ad_groups': SingleKeywordAdGroupsEvidence,
  'keywords.match_type_distribution': MatchTypeDistributionEvidence,
  'keywords.duplicate_keywords': DuplicateKeywordsEvidence,
  'negatives.account_level_coverage': AccountLevelNegativeCoverageEvidence,
  'negatives.restrictive': RestrictiveNegativesEvidence,
  'negatives.ai_max_brand_exclusions': AiMaxBrandExclusionsEvidence,
  'pmax.ad_strength': PmaxAdStrengthEvidence,
  'pmax.asset_coverage': PmaxAssetGroupCoverageEvidence,
  'pmax.audience_signals': PmaxAudienceSignalsEvidence,
  'pmax.search_themes': PmaxSearchThemesEvidence,
  'pmax.brand_exclusions': PmaxBrandExclusionsEvidence,
  'pmax.final_url_expansion': PmaxFinalUrlExpansionEvidence,
  'pmax.brand_guidelines': PmaxBrandGuidelinesEvidence,
  'pmax.text_customization': PmaxToggleSettingEvidence,
  'pmax.video_enhancements': PmaxToggleSettingEvidence,
  'pmax.bid_strategy': PmaxBidStrategyEvidence,
  'pmax.budget_constraints': PmaxBudgetConstraintsEvidence,
  'pmax.goal_selection': PmaxGoalSelectionEvidence,
  'pmax.listing_groups': PmaxListingGroupsEvidence,
  'pmax.structure': PmaxStructureEvidence,
  'shopping.campaign_priority': ShoppingCampaignPriorityEvidence,
  'shopping.bid_strategy': ShoppingBidStrategyEvidence,
  'shopping.budget_constraints': ShoppingBudgetConstraintsEvidence,
  'shopping.goal_selection': ShoppingGoalSelectionEvidence,
  'shopping.brand_exclusions': ShoppingBrandExclusionsEvidence,
  'shopping.product_feed_health': ProductFeedHealthEvidence,
  'shopping.feed_attribute_coverage': FeedAttributeCoverageEvidence,
  'shopping.title_quality': TitleQualityEvidence,
  'shopping.listing_groups': ListingGroupsEvidence,
  'shopping.account_issues': AccountIssuesEvidence
};

export function findEvidenceRenderer(
  checkId: string,
  platform: Platform,
  isJudgmentRow: boolean
): FC<EvidenceProps> {
  const specific = PLATFORM_SPECIFIC[checkId]?.[platform];
  if (specific) return specific;
  const agnostic = PLATFORM_AGNOSTIC[checkId];
  if (agnostic) return agnostic;
  // For judgment-row checks that don't have a dedicated renderer yet, the generic LLM
  // verdict display is a better fallback than dumping JSON.
  if (isJudgmentRow) return GenericLlmJudgmentEvidence;
  return DefaultEvidence;
}
